import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocaleSelector from './LocaleSelector';

// Add ChevronDown icon component
const ChevronDownIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 20 20" 
    fill="currentColor" 
    className={className}
  >
    <path 
      fillRule="evenodd" 
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" 
      clipRule="evenodd" 
    />
  </svg>
);

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('home');
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const navRefs = useRef({});
  const location = useLocation();
  const navigate = useNavigate();
  const desktopDropdownRef = useRef(null);
  const desktopDropdownParentRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const mobileDropdownParentRef = useRef(null);

  useEffect(() => {
    // Update active item based on current path
    const path = location.pathname;
    const hash = location.hash;
    
    if (path === '/') {
      // Only set to home if there's no hash and we're not in the middle of a section navigation
      if (!hash) {
        setActiveItem('home');
      } else if (hash === '#hakkimizda') {
        setActiveItem('about');
      } else if (hash === '#faaliyet') {
        setActiveItem('faaliyet');
      } else if (hash === '#degerler') {
        setActiveItem('degerler');
      }
    } else if (path === '/iletisim') {
      setActiveItem('contact');
    } else if (['/tekstil', '/ambalaj', '/ticaret'].includes(path)) {
      setActiveItem('faaliyet');
    }

    // Handle hash navigation when page loads
    if (hash && path === '/') {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          const navHeight = 64;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle desktop dropdown
      if (isDesktopDropdownOpen) {
        const isOutsideDesktopDropdown = desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target);
        const isOutsideDesktopButton = desktopDropdownParentRef.current && !desktopDropdownParentRef.current.contains(event.target);
        
        if (isOutsideDesktopDropdown && isOutsideDesktopButton) {
          setIsDesktopDropdownOpen(false);
        }
      }

      // Handle mobile dropdown
      if (isMobileDropdownOpen) {
        const isOutsideMobileDropdown = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target);
        const isOutsideMobileButton = mobileDropdownParentRef.current && !mobileDropdownParentRef.current.contains(event.target);
        
        if (isOutsideMobileDropdown && isOutsideMobileButton) {
          setIsMobileDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDesktopDropdownOpen, isMobileDropdownOpen]);

  useEffect(() => {
    const updateIndicator = () => {
      const activeElement = navRefs.current[activeItem];
      if (activeElement) {
        const textSpan = activeElement.querySelector('span');
        if (!textSpan) return;

        const spanRect = textSpan.getBoundingClientRect();
        const navRect = activeElement.getBoundingClientRect();
        const parentRect = activeElement.parentElement.getBoundingClientRect();
        
        const isRTL = document.documentElement.dir === 'rtl';
        if (isRTL) {
          // Calculate the center position of the text span relative to its parent
          const spanCenter = spanRect.left + (spanRect.width / 2);
          const parentLeft = parentRect.left;
          const position = spanCenter - parentLeft - (spanRect.width / 2);
          
          setIndicatorStyle({
            right: 'auto',
            left: `${position}px`,
            width: `${spanRect.width}px`
          });
        } else {
          const leftPosition = navRect.left - parentRect.left + (navRect.width - spanRect.width) / 2;
          setIndicatorStyle({
            left: `${leftPosition}px`,
            right: 'auto',
            width: `${spanRect.width}px`
          });
        }
      }
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeItem]);

  useEffect(() => {
    let timeoutId;
    
    const handleScroll = () => {
      if (location.pathname !== '/') return;

      // Clear the previous timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set a new timeout
      timeoutId = setTimeout(() => {
        const sections = {
          home: { id: 'hero', name: 'home' },
          about: { id: 'hakkimizda', name: 'about' },
          faaliyet: { id: 'faaliyet', name: 'faaliyet' },
          degerler: { id: 'degerler', name: 'degerler' }
        };

        // Get all sections
        const sectionElements = Object.values(sections).map(section => ({
          element: document.getElementById(section.id),
          name: section.name
        })).filter(section => section.element);

        // If we're at the very top of the page, set home as active
        if (window.scrollY < 100) {
          setActiveItem('home');
          return;
        }

        // Calculate which section is in view
        const navHeight = 64; // Height of navbar
        let currentSection = null;

        for (const section of sectionElements) {
          const rect = section.element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          // Section is considered "in view" when its top portion (40% of viewport) is visible
          // and at least 100px of the section is visible
          if (rect.top <= navHeight + (viewportHeight * 0.4) && 
              rect.bottom >= navHeight + 100) {
            currentSection = section.name;
            break;
          }
        }

        if (currentSection) {
          setActiveItem(currentSection);
        }
      }, 50); // 50ms debounce
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.pathname]);

  const handleScroll = async (e, item) => {
    e.preventDefault();

    // For activity pages direct navigation
    if (item.path) {
      setActiveItem('faaliyet');
      navigate(item.path);
      setIsMobileDropdownOpen(false);
      return;
    }

    // For non-hash links (like home and contact)
    if (!item.href.startsWith('#')) {
      if (item.href === '/') {
        setActiveItem(item.name);
        if (location.pathname !== '/') {
          setTimeout(() => {
            navigate(item.href);
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }, 300);
        } else {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      } else {
        setActiveItem(item.name);
        setTimeout(() => {
          navigate(item.href);
        }, 300);
      }
      return;
    }

    // For hash links (sections within home page)
    setActiveItem(item.name);
    if (location.pathname !== '/') {
      navigate('/' + item.href);
    } else {
      const element = document.querySelector(item.href);
      if (element) {
        const navHeight = 64;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleDesktopDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDesktopDropdownOpen(prev => !prev);
  };

  const handleMobileDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileDropdownOpen(prev => !prev);
  };

  const activityPages = [
    { name: 'textile', label: t('activities.areas.textile.title'), path: '/tekstil' },
    { name: 'packaging', label: t('activities.areas.packaging.title'), path: '/ambalaj' },
    { name: 'trade', label: t('activities.areas.trade.title'), path: '/ticaret' }
  ];

  const navItems = [
    { name: 'home', label: t('navbar.home'), href: '/' },
    { name: 'about', label: t('navbar.about'), href: '#hakkimizda' },
    { name: 'faaliyet', label: t('navbar.activities'), href: '#faaliyet', hasDropdown: true },
    { name: 'degerler', label: t('navbar.values'), href: '#degerler' },
    { name: 'catalog', label: t('navbar.catalog'), href: '/katalog' },
    { name: 'contact', label: t('navbar.contact'), href: '/iletisim' }
  ];

  return (
    <nav className="bg-white fixed w-full z-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and company name */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex-shrink-0 flex items-center"
              onClick={(e) => handleScroll(e, { name: 'home', href: '/' })}
            >
              <img 
                src="/images/logo.png" 
                alt="Orbitras" 
                className="h-8 w-auto"  // Adjust height as needed
              />
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center justify-end flex-1">
            <div className="flex items-baseline h-16 relative">
              {navItems.map((item) => (
                <div
                  key={item.name}
                  ref={el => {
                    navRefs.current[item.name] = el;
                    if (item.hasDropdown) {
                      desktopDropdownParentRef.current = el;
                    }
                  }}
                  className="relative h-full"
                >
                  <a
                    href={item.href}
                    onClick={(e) => {
                      if (item.hasDropdown) {
                        handleDesktopDropdownClick(e);
                      } else {
                        handleScroll(e, item);
                      }
                    }}
                    className={`h-full flex items-center justify-center px-4 text-sm font-medium transition-colors duration-200 ${
                      activeItem === item.name 
                        ? 'text-gray-900' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <span className="inline-block whitespace-nowrap">{item.label}</span>
                    {item.hasDropdown && (
                      <ChevronDownIcon className={`ml-1 w-4 h-4 transition-transform duration-200 ${isDesktopDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </a>
                  {item.hasDropdown && isDesktopDropdownOpen && (
                    <div 
                      ref={desktopDropdownRef}
                      className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-md py-1 mt-1"
                    >
                      {activityPages.map((activity) => (
                        <a
                          key={activity.name}
                          href={activity.path}
                          onClick={(e) => {
                            handleScroll(e, activity);
                            setIsDesktopDropdownOpen(false);
                          }}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {activity.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {/* Active indicator */}
              <div 
                className="absolute bottom-[-1px] h-[3px] bg-[#1365c4] transition-all duration-300 ease-in-out z-10"
                style={indicatorStyle}
              />
            </div>
            {/* LocaleSelector */}
            <div className="hidden md:flex items-center ml-6">
              <LocaleSelector />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {!isOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isOpen ? 'block' : 'hidden'} md:hidden border-t border-gray-200`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navItems.map((item) => (
            <React.Fragment key={item.name}>
              <a
                ref={item.hasDropdown ? mobileDropdownParentRef : null}
                href={item.href}
                onClick={(e) => {
                  if (item.hasDropdown) {
                    handleMobileDropdownClick(e);
                  } else {
                    handleScroll(e, item);
                    setIsOpen(false);
                  }
                }}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  activeItem === item.name 
                    ? 'text-gray-900 bg-gray-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{item.label}</span>
                  {item.hasDropdown && (
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </a>
              {item.hasDropdown && isMobileDropdownOpen && (
                <div 
                  ref={mobileDropdownRef}
                  className="pl-6"
                >
                  {activityPages.map((activity) => (
                    <a
                      key={activity.name}
                      href={activity.path}
                      onClick={(e) => {
                        handleScroll(e, activity);
                        setIsOpen(false);
                        setIsMobileDropdownOpen(false);
                      }}
                      className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    >
                      {activity.label}
                    </a>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
          {/* Added LocaleSelector for mobile */}
          <div className="px-3 py-2">
            <LocaleSelector />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 